//frontend/src/App.jsx
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/dashboard/Dashboard';

// System Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import Companies from './pages/admin/Companies';
import SystemUsers from './pages/admin/SystemUsers';
import CompanyDetails from './pages/admin/CompanyDetails';
import Reports from './pages/admin/Reports';
import Settings from './pages/admin/Settings';
import SystemSettlements from './pages/admin/SystemSettlements';

// Company Admin
import CompanyDashboard from './pages/company/CompanyDashboard';
import Warehouses from './pages/company/Warehouses';
import WarehouseDetails from './pages/company/WarehouseDetails';
import Shops from './pages/company/Shops';
import Categories from './pages/company/Categories';
import Products from './pages/company/Products';
import ProductVariants from './pages/company/ProductVariants';
import InventoryReceipt from './pages/company/InventoryReceipt';
import InventoryOutbound from './pages/company/InventoryOutbound';
import DeliveryOrders from './pages/company/DeliveryOrders';
import DeliveryZones from './pages/company/DeliveryZones';
import DriversManagement from './pages/company/DriversManagement';
import CompanySettlements from './pages/company/CompanySettlements'; 
import CompanySettings from './pages/company/CompanySettings';
import CompanyUsers from './pages/company/Users';

// Shop Admin
import ShopDashboard from './pages/shop/Dashboard';
import ShopDeliveries from './pages/shop/Deliveries';
import CreateDelivery from './pages/shop/CreateDelivery';
import ShopMySettlement from './pages/shop/MySettlement';
import ShopCodPayments from './pages/shop/ShopCodPayments';
import ShopProducts from './pages/shop/Products';
import ShopSettings from './pages/shop/ShopSettings';

// Driver
import DriverLayout from './components/layout/DriverLayout';
import DriverDashboard from './pages/driver/DriverDashboard';
import DriverDeliveries from './pages/driver/DriverDeliveries';
import DriverCOD from './pages/driver/DriverCOD';
import DriverInventory from './pages/driver/DriverInventory';

// Layout
import CompanyLayout from './components/layout/CompanyLayout';

// ============================================
// PROTECTED ROUTE COMPONENT
// ============================================
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-xl text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.user_type)) {
    return <Navigate to="/unauthorized" />;
  }
  
  return children;
};

// ============================================
// PUBLIC ROUTE COMPONENT
// ============================================
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-xl text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    switch (user?.user_type) {
      case 'system_admin':
        return <Navigate to="/admin/dashboard" />;
      case 'company_user':
        return <Navigate to="/company/dashboard" />;
      case 'shop_user':
        return <Navigate to="/shop/dashboard" />;
      case 'driver':
        return <Navigate to="/driver/deliveries" />;
      default:
        return <Navigate to="/dashboard" />;
    }
  }
  
  return children;
};

// ============================================
// UNAUTHORIZED PAGE
// ============================================
const Unauthorized = () => {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-red-500 mb-4">403</h1>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Хандах эрхгүй</h2>
        <p className="text-gray-600 mb-6">Та энэ хуудсанд нэвтрэх эрхгүй байна.</p>
        <button onClick={logout} className="btn btn-primary">Буцах</button>
      </div>
    </div>
  );
};

// ============================================
// PLACEHOLDER PAGE
// ============================================
const PlaceholderPage = ({ title, icon }) => (
  <CompanyLayout>
    <div className="p-8 text-center">
      <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
        <span className="text-3xl">{icon}</span>
        <span>{title}</span>
      </h1>
      <p className="text-gray-600 mt-2">Тун удахгүй...</p>
    </div>
  </CompanyLayout>
);

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Root & Login */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />

          {/* ============================================ */}
          {/* SYSTEM ADMIN ROUTES */}
          {/* ============================================ */}
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['system_admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/companies" 
            element={
              <ProtectedRoute allowedRoles={['system_admin']}>
                <Companies />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/companies/:id" 
            element={
              <ProtectedRoute allowedRoles={['system_admin']}>
                <CompanyDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/reports" 
            element={
              <ProtectedRoute allowedRoles={['system_admin']}>
                <Reports />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedRoute allowedRoles={['system_admin']}>
                <Settings />
              </ProtectedRoute>
            } 
          />
           <Route 
             path="/admin/settlements" 
             element={
               <ProtectedRoute allowedRoles={['system_admin']}>
                <SystemSettlements />
              </ProtectedRoute>} />
          
          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute allowedRoles={['system_admin']}>
                <SystemUsers />
              </ProtectedRoute>
            } 
          />

          {/* ============================================ */}
          {/* COMPANY ADMIN ROUTES */}
          {/* ============================================ */}
          <Route 
            path="/company/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <CompanyDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* 🏭 Агуулах */}
          <Route 
            path="/company/warehouses" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <Warehouses />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/company/warehouses/:id" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <WarehouseDetails />
              </ProtectedRoute>
            } 
          />
          
          {/* 🛍️ Онлайн шоп */}
          <Route 
            path="/company/shops" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <Shops />
              </ProtectedRoute>
            } 
          />
          <Route 
          path="/shop/settlement" 
          element={
            <ProtectedRoute allowedRoles={['shop_user']}>
              <ShopMySettlement />
            </ProtectedRoute>
          } 
        />
         <Route 
            path="/shop/cod-payments" 
            element={
              <ProtectedRoute allowedRoles={['shop_user']}>
                <ShopCodPayments />
              </ProtectedRoute>
            } 
          />
        
          {/* 📂 Ангилал */}
          <Route 
            path="/company/categories" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <Categories />
              </ProtectedRoute>
            } 
          />
          
          {/* 📦 Бараа */}
          <Route 
            path="/company/products" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <Products />
              </ProtectedRoute>
            } 
          />

          {/* 🎨 Барааны варианууд */}
          <Route 
            path="/company/products/:id/variants" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <ProductVariants />
              </ProtectedRoute>
            } 
          />

          {/* 📥 Орлого */}
          <Route 
            path="/company/inventory/receipt" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <InventoryReceipt />
              </ProtectedRoute>
            } 
          />
          
          {/* 📤 Зарлага */}
          <Route 
            path="/company/inventory/outbound" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <InventoryOutbound />
              </ProtectedRoute>
            } 
          />
          
          {/* 🚚 Хүргэлт */}
          <Route 
            path="/company/deliveries" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <DeliveryOrders />
              </ProtectedRoute>
            } 
          />

          {/* 👨‍✈️ ЖОЛООЧИЙН УДИРДЛАГА */}
          <Route 
            path="/company/drivers" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <DriversManagement />
              </ProtectedRoute>
            } 
          />

          {/* 🗺️ БҮСЧЛЭЛ */}
          <Route 
            path="/company/zones" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <DeliveryZones />
              </ProtectedRoute>
            } 
          />

          {/* 💰 ТӨЛБӨР ТООЦОО - Placeholder (дараа бодит component болгоно) */}
          <Route 
            path="/company/settlements" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <CompanySettlements />
              </ProtectedRoute>
            } 
          />
          
          {/* 👥 ХЭРЭГЛЭГЧИД */}
          <Route 
            path="/company/users" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <CompanyLayout>
                  <CompanyUsers />
                </CompanyLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/company/reports" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <PlaceholderPage title="Тайлан" icon="📊" />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/company/settings" 
            element={
              <ProtectedRoute allowedRoles={['company_user']}>
                <CompanySettings />
              </ProtectedRoute>
            } 
          />

          {/* ============================================ */}
          {/* SHOP ADMIN ROUTES */}
          {/* ============================================ */}
          <Route 
            path="/shop/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['shop_user']}>
                <ShopDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/shop/deliveries" 
            element={
              <ProtectedRoute allowedRoles={['shop_user']}>
                <ShopDeliveries />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/shop/deliveries/create" 
            element={
              <ProtectedRoute allowedRoles={['shop_user']}>
                <CreateDelivery />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/shop/products" 
            element={
              <ProtectedRoute allowedRoles={['shop_user']}>
                <ShopProducts />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/shop/settings" 
            element={
              <ProtectedRoute allowedRoles={['shop_user']}>
                <ShopSettings />
              </ProtectedRoute>
            } 
          />
           {/* ============================================ */}
          {/* DRIVER ROUTES */}
          <Route 
            path="/driver" 
            element={
              <ProtectedRoute allowedRoles={['driver']}>
                <DriverLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/driver/dashboard" />} />
            <Route path="dashboard" element={<DriverDashboard />} />
            <Route path="deliveries" element={<DriverDeliveries />} />
            <Route path="cod" element={<DriverCOD />} />
            <Route path="inventory" element={<DriverInventory />} />
          </Route>

          {/* ============================================ */}
          {/* LEGACY ROUTES */}
          {/* ============================================ */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />

          {/* ============================================ */}
          {/* ERROR ROUTES */}
          {/* ============================================ */}
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;